<template>
  <ReadOnlyItem
    v-if="disabled"
    :gray="gray"
    :label="title"
    :value="selectedOption?.label || placeholder"
  ></ReadOnlyItem>
  <IonItem
    v-else
    class="inline-select-item"
    :color="gray ? 'whitesmoke' : 'white'"
    lines="full"
    :button="!disabled"
    :detail="false"
    @click="onItemClicked"
  >
    <div slot="start" class="flex items-center h-[60px] truncate">
      <ValidationIcon :valid-icon="icon" :valid-icon-color="iconColor" :state="state" :errors="errors"></ValidationIcon>
      <div class="font-medium text-gray-500">{{ title }}</div>
    </div>
    <div slot="end" class="flex items-center h-[60px] truncate">
      <div v-if="selectedOption" class="font-medium text-gray-700">{{ selectedOption.label }}</div>
      <div
        v-else-if="placeholder"
        class="font-medium"
        :class="{
          'text-blue-500': placeholderColor === 'blue',
          'text-gray-500': !placeholderColor || placeholderColor === 'light',
        }"
      >
        {{ placeholder }}
      </div>
      <IconButton
        :disabled="disabled"
        class="ml-2"
        @click="selectedOption && clearable ? onClearClicked($event) : null"
        color="medium"
        size="small"
        :icon="selectedOption && clearable ? 'icon-x' : 'icon-chevron-right'"
      ></IconButton>
    </div>
  </IonItem>
  <IonAlert ref="alert" :header="title" :inputs="alertInputs" :buttons="alertButtons"></IonAlert>
</template>

<script setup lang="ts">
import { AlertButton, AlertInput, IonAlert, IonItem } from '@ionic/vue';
import { SelectOption } from '../select';
import { computed, ref } from 'vue';
import IconButton from '../IconButton.vue';
import ValidationIcon from '../ValidationIcon.vue';
import { FormControlError, FormControlState } from '@/composables/use-form-control';
import ReadOnlyItem from '../ReadOnlyItem.vue';

const props = withDefaults(
  defineProps<{
    options: SelectOption[];
    title: string;
    placeholder?: string;
    placeholderColor?: 'light' | 'blue';
    gray?: boolean;
    icon?: string;
    iconColor?: 'gray' | 'blue';
    clearable?: boolean;
    clearBehavior?: 'clear' | 'prompt';
    state?: FormControlState;
    errors?: FormControlError[];
    disabled?: boolean;
  }>(),
  { clearable: true, clearBehavior: 'clear', iconColor: 'blue' }
);

const emits = defineEmits<{
  (e: 'selected', val: unknown): void;
  (e: 'cleared'): void;
}>();

const alert = ref();
const vModel = defineModel();
const selectedOption = computed(() =>
  vModel.value !== undefined ? props.options.find((o) => o.value === vModel.value) : undefined
);

const alertButtons: AlertButton[] = [
  {
    text: 'Cancel',
    role: 'cancel',
  },
  {
    text: 'Ok',
    role: 'confirm',
    handler: (val) => {
      vModel.value = val;
      emits('selected', val);
    },
  },
];

const alertInputs = computed<AlertInput[]>(() => {
  return props.options.map((o) => {
    return {
      label: o.label,
      checked: vModel.value === o.value,
      type: 'radio',
      value: o.value,
      disabled: o.disabled,
    };
  });
});

function onClearClicked(ev: MouseEvent) {
  ev.stopPropagation();
  ev.preventDefault();
  if (props.disabled) {
    return;
  }
  if (props.clearBehavior === 'clear') {
    vModel.value = undefined;
  }
  emits('cleared');
}

function onItemClicked() {
  if (props.disabled) {
    return;
  }
  alert.value?.$el.present();
}
</script>

<style scoped>
ion-item.inline-select-item::part(native) {
  --border-width: 1px;
}

ion-item.inline-select-item {
  --padding-start: 0px;
}
</style>
